import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Manager } from 'socket.io-client'
import { useUserContext } from '../../context/users/UserContext'
import { Card, Modal, Button, Badge } from 'react-bootstrap'
import { getByIdPA, patchByIdPA, getDomain } from '../../context/users/UserActions'
import { 
  FaQuestionCircle, 
  FaImages, 
  FaCheckCircle,
  FaUser,
  FaClock,
  FaComments,
  FaHourglassHalf,
  FaInbox,
  FaSpinner,
  FaWifi,
  FaExclamationCircle,
  FaPhone,
  FaEye,
  FaUsers,
  FaTimes,
  FaExpandAlt,
  FaSignOutAlt
} from 'react-icons/fa'
import ResponseCanvas from './ResponseCanvas'
import { messageForGuestSessionAccess } from '../../utils/sessionGuestMessages'
import './GuestCanvas.css'

const listenerServiceUrl = process.env.REACT_APP_LISTENER_SERVICE || window.location.origin
const manager = new Manager(listenerServiceUrl)
const socket = manager.socket('/')

const sessionStorageKey = process.env.REACT_APP_SESSION_KEY || 'alleanza_jwtoken'

/** Salir de sesión (invitado): misma ventana, siempre a Develemos. */
const INVITADO_SALIR_SESION_URL = 'https://develemos.com/'

export default function GuestCanvas() {
  const { session, setSession, authenticatedUser, setAlert, logOutUser } = useUserContext()
  const [validSession, setValidSession] = useState(false)
  const [message, setMessage] = useState('')
  const [sessionOpen, setSessionOpen] = useState(true)
  const [sessionStartTime, setSessionStartTime] = useState(null)
  const [sessionDuration, setSessionDuration] = useState('00:00')
  
  // Estados para manejo de carga
  const [isLoading, setIsLoading] = useState(true)
  const [loadingTimeout, setLoadingTimeout] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('Conectando con la sesión...')

  // Estados para respuestas
  const [showModal, setShowModal] = useState(false)
  const [userIndex, setUserIndex] = useState(0)
  const [questionId, setQuestionId] = useState(0)
  const [questionIndex, setQuestionIndex] = useState(0)
  const [qstResponses, setQuestionResponses] = useState([])
  const [imagesSelected, setImagesSelected] = useState([])
  const [userAnswers, setUserAnswers] = useState([])
  
  // Estados para modal de respuestas de todos
  const [showAllResponsesModal, setShowAllResponsesModal] = useState(false)
  const [selectedQuestionForView, setSelectedQuestionForView] = useState(null)
  const [showImageDetailModal, setShowImageDetailModal] = useState(false)
  const [selectedImageDetail, setSelectedImageDetail] = useState(null)

  const params = useParams()

  const isInvitado =
    Array.isArray(authenticatedUser?.permissions) &&
    authenticatedUser.permissions.includes('INVITADO')

  const handleExitToLogin = () => {
    localStorage.removeItem(sessionStorageKey)
    try {
      localStorage.removeItem('alleanza_currentSession')
    } catch (e) {
      // ignore
    }
    logOutUser({})
    window.location.assign(INVITADO_SALIR_SESION_URL)
  }

  // Timer de duración de sesión
  useEffect(() => {
    if (sessionStartTime) {
      const interval = setInterval(() => {
        const now = new Date()
        const diff = now - sessionStartTime
        const minutes = Math.floor(diff / 60000)
        const seconds = Math.floor((diff % 60000) / 1000)
        setSessionDuration(`${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
      }, 1000)

      return () => clearInterval(interval)
    }
  }, [sessionStartTime])

  // Polling para actualizar datos de sesión cada 4 segundos
  useEffect(() => {
    if (validSession && session?.sessionId) {
      const fetchSessionData = async () => {
        try {
          const sessionData = await getByIdPA({
            path: 'canvas',
            id: params.id,
            token: authenticatedUser.value || authenticatedUser.token
          })
          
          if (Object.prototype.hasOwnProperty.call(sessionData, 'sessionId')) {
            setSession(sessionData)
          }
        } catch (error) {
          console.log('Error al actualizar datos de sesión:', error)
        }
      }

      // Ejecutar inmediatamente
      fetchSessionData()
      
      // Configurar intervalo de 4 segundos
      const interval = setInterval(fetchSessionData, 4000)

      return () => clearInterval(interval)
    }
  }, [validSession, session?.sessionId, params.id, authenticatedUser.value, authenticatedUser.token])

  const fetchData = async () => {
    try {
      setIsLoading(true)
      setLoadingTimeout(false)
      setLoadingMessage('Conectando con la sesión...')
      
      // Timeout de 60 segundos
      const timeoutId = setTimeout(() => {
        if (isLoading) {
          setLoadingTimeout(true)
          setLoadingMessage('Error de conexión')
        }
      }, 60000)
      
      const sessionData = await getByIdPA({ 
        path: 'canvas', 
        id: params.id, 
        token: authenticatedUser.value || authenticatedUser.token 
      })
      
      clearTimeout(timeoutId)
      
      if (Object.prototype.hasOwnProperty.call(sessionData, 'sessionId')) {
        setLoadingMessage('Sesión cargada exitosamente')
        
        socket.emit('Canvas', { 
          session: params.id, 
          type: 'START_SESSION', 
          user: sessionData.user, 
          userId: authenticatedUser.userId 
        })
        
        sessionData.sessionUsers = (sessionData?.sessionUsers || []).filter((user) => user.userRole === 'INVITADO')
        setSession(sessionData)
        setValidSession(true)
        setSessionStartTime(new Date())
        
        localStorage.setItem('alleanza_currentSession', JSON.stringify(sessionData))
        
        // Pequeño delay para mostrar el mensaje de éxito
        setTimeout(() => {
          setIsLoading(false)
        }, 500)
      } else {
        setMessage(
          messageForGuestSessionAccess(sessionData.message, sessionData) ||
            'Error al cargar la sesión'
        )
        setIsLoading(false)
      }
    } catch (error) {
      console.error('Error fetching session data:', error)
      setLoadingTimeout(true)
      setLoadingMessage('Error de conexión')
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Función para mostrar modal de respuesta
  const handleShow = (questionId, userIndex, questionIndex, canAnswer, responses, userResponses) => {
    if (canAnswer) {
      setQuestionId(questionId)
      setUserIndex(userIndex)
      setQuestionIndex(questionIndex)
      setUserAnswers(userResponses)
      setImagesSelected(userResponses.map((image) => (image.imageId)))
      
      const updatedResponses = responses.map((response) => ({
        ...response,
        phrase: getPhrase(response.imageId, userResponses)
      }))
      setQuestionResponses(updatedResponses)
      setShowModal(true)
    }
  }

  const getPhrase = (id, userResponses) => {
    let phrase = ''
    const questionData = userResponses.filter((response) => response.imageId === id)
    if (questionData.length > 0) {
      phrase = questionData[0].phrase
    }
    return phrase
  }

  const handleCloseModal = () => setShowModal(false)

  const handleOnSelectResponses = ({ imageId, response }) => {
    if (!imagesSelected.includes(imageId)) {
      const answer = {
        sessionQstId: response.sessionQstId,
        imageId: response.imageId,
        image: response.image,
        phrase: ''
      }
      setUserAnswers([...userAnswers, answer])
      setImagesSelected([...imagesSelected, imageId]);
    } else {
      setUserAnswers((prevState) => prevState.filter((answer) => answer.imageId !== imageId));
      setImagesSelected((prevState) => prevState.filter((image) => image !== imageId));
    }
  }

  const handleChangePhrase = async (e, index, imageId) => {
    const phraseTmp = e.target.value
    const temp = userAnswers
    const imgIndex = userAnswers.findIndex((userAnswer) => userAnswer.imageId === imageId)
    temp[imgIndex].phrase = phraseTmp
    setUserAnswers(temp)

    qstResponses[index].phrase = phraseTmp
    setQuestionResponses(qstResponses)
  }

  const saveResponse = async () => {
    if (userAnswers.length > 0) {
      let record = await patchByIdPA({
        path: 'answers',
        id: session.sessionId || 0,
        data: {
          questionId,
          sessionQstId: userAnswers[0].sessionQstId,
          responses: userAnswers,
        },
        token: authenticatedUser.value || authenticatedUser.token
      })

      if (Object.prototype.hasOwnProperty.call(record, 'sessionId')) {
        // Actualizar la sesión correctamente
        const updatedSession = { ...session }
        const currentUser = updatedSession.sessionUsers.find(user => user.userId === authenticatedUser.userId)
        
        if (currentUser) {
          const answerIndex = currentUser.userAnswers.findIndex(answer => answer.sessionQstId === userAnswers[0].sessionQstId)
          if (answerIndex !== -1) {
            currentUser.userAnswers[answerIndex].responses = [...userAnswers]
          }
        }
        
        setSession(updatedSession)
        
        socket.emit('Canvas', { 
          session: params.id, 
          type: 'UPDATE_ANSWER', 
          user: currentUser, 
          userId: authenticatedUser.userId 
        })
        
        setShowModal(false)
        setAlert({
          message: '¡Respuesta guardada exitosamente!',
          msgType: 'success'
        })
      } else {
        setAlert({
          message: record.message || 'Error al guardar la respuesta',
          msgType: 'warning'
        })
      }
      setUserAnswers([])
      setImagesSelected([])
    } else {
      setAlert({
        message: 'Por favor selecciona al menos una imagen',
        msgType: 'warning'
      })
    }
  }

  // Manejo de eventos de socket
  const handleEvent = (event) => {
    if (params.id === event.session && session) {
      if (event.userId !== authenticatedUser.userId) {
        switch (event.type) {
          case 'ADD_QUESTION':
            const question = event.question
            if (question) {
              setAlert({
                message: `Nueva pregunta agregada: "${question.question}"`,
                msgType: 'success'
              })
              
              const questionExists = session.questions.some((item) => item.sessionQstId === question.sessionQstId)
              if (!questionExists) {
                setSession(prevSession => {
                  const newSession = { ...prevSession }
                  newSession.questions = [...newSession.questions, question]
                  
                  // Actualizar las preguntas del usuario actual
                  const currentUser = newSession.sessionUsers.find(user => user.userId === authenticatedUser.userId)
                  if (currentUser) {
                    const userQuestionExists = currentUser.userAnswers.some((answer) => answer.sessionQstId === question.sessionQstId)
                    if (!userQuestionExists) {
                      currentUser.userAnswers = [...currentUser.userAnswers, {
                        sessionId: newSession.sessionId,
                        sessionCode: newSession.sessionCode,
                        userId: authenticatedUser.userId,
                        ...question,
                        responses: []
                      }]
                    }
                  }
                  
                  return newSession
                })
              }
            }
            break

          case 'REMOVE_QUESTION':
            const sessionQstId = event.sessionQstId
            if (sessionQstId) {
              setAlert({
                message: 'Una pregunta ha sido eliminada de la sesión',
                msgType: 'info'
              })
              
              setSession(prevSession => {
                const newSession = { ...prevSession }
                newSession.questions = newSession.questions.filter((question) => question.sessionQstId !== sessionQstId)
                
                // Remover la pregunta de las respuestas del usuario actual
                const currentUser = newSession.sessionUsers.find(user => user.userId === authenticatedUser.userId)
                if (currentUser) {
                  currentUser.userAnswers = currentUser.userAnswers.filter((answer) => answer.sessionQstId !== sessionQstId)
                }
                
                return newSession
              })
            }
            break

          case 'CLOSE_SESSION':
            setAlert({
              message: 'La sesión ha sido finalizada por el anfitrión. ¡Gracias por participar!',
              msgType: 'info'
            })
            setSessionOpen(false)
            
            setTimeout(() => {
              var currentProtocol = window.location.protocol
              var currentDomain = getDomain()
              var encuestaURL = currentProtocol + '//encuesta.' + currentDomain + '/encuesta/responder/1'
              window.open(encuestaURL, '_self')
            }, 3000)
            break

          default:
            break
        }
      }
    }
  }

  // Configurar listeners de socket
  useEffect(() => {
    const handleUsuarioEvent = (event) => {
      handleEvent(event)
    }

    socket.on('Usuario', handleUsuarioEvent)

    // Cleanup function
    return () => {
      socket.off('Usuario', handleUsuarioEvent)
    }
  }, [session, authenticatedUser.userId, params.id])

  // Obtener el usuario actual
  const getCurrentUser = () => {
    return session.sessionUsers?.find(user => user.userId === authenticatedUser.userId)
  }

  // Verificar si una pregunta ha sido respondida
  const isQuestionAnswered = (question) => {
    const currentUser = getCurrentUser()
    if (!currentUser) return false
    
    const userAnswer = currentUser.userAnswers?.find(answer => answer.sessionQstId === question.sessionQstId)
    return userAnswer && userAnswer.responses && userAnswer.responses.length > 0
  }

  // Obtener estadísticas de progreso personal
  const getPersonalProgress = () => {
    const currentUser = getCurrentUser()
    if (!currentUser || !session.questions) return { answered: 0, total: 0 }
    
    const totalQuestions = session.questions.length
    const answeredQuestions = session.questions.filter(question => isQuestionAnswered(question)).length
    
    return { answered: answeredQuestions, total: totalQuestions }
  }

  // Funciones para modal de respuestas de todos
  const handleOpenAllResponsesModal = () => {
    setShowAllResponsesModal(true)
    // Si hay preguntas, seleccionar la primera por defecto
    if (session.questions && session.questions.length > 0) {
      setSelectedQuestionForView(session.questions[0])
    }
  }

  const handleCloseAllResponsesModal = () => {
    setShowAllResponsesModal(false)
    setSelectedQuestionForView(null)
  }

  const handleSelectQuestionForView = (question) => {
    setSelectedQuestionForView(question)
  }

  const handleImageDetailClick = (image, userName, phrase, questionText) => {
    setSelectedImageDetail({
      src: image,
      userName,
      phrase,
      questionText
    })
    setShowImageDetailModal(true)
  }

  const handleCloseImageDetail = () => {
    setShowImageDetailModal(false)
    setSelectedImageDetail(null)
  }

  // Pantalla de carga
  if (isLoading && !loadingTimeout) {
    return (
      <div className="guest-canvas-container">
        <div className="loading-state">
          <div className="loading-content">
            <div className="loading-icon">
              <FaSpinner className="spinning" />
            </div>
            <h2 className="loading-title">Conectando con la sesión</h2>
            <p className="loading-message">{loadingMessage}</p>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Pantalla de error después del timeout
  if (loadingTimeout) {
    return (
      <div className="guest-canvas-container">
        <div className="error-state-friendly">
          <div className="error-content">
            <div className="error-icon">
              <FaWifi className="wifi-icon" />
            </div>
            <h2 className="error-title">Problema de conexión</h2>
            <p className="error-message">
              No pudimos conectar con la sesión. Esto puede deberse a problemas de red o del servidor.
            </p>
            <div className="error-actions">
              <Button
                variant="primary"
                onClick={() => {
                  setIsLoading(true)
                  setLoadingTimeout(false)
                  fetchData()
                }}
                className="retry-btn"
              >
                <FaSpinner className="me-2" />
                Intentar de nuevo
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => window.location.href = '/'}
                className="home-btn"
              >
                Volver al inicio
              </Button>
            </div>
            <div className="error-help">
              <FaExclamationCircle className="help-icon" />
              <p>Si el problema persiste, contacta al administrador del sistema</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Pantalla de error de sesión inválida
  if (!validSession) {
    return (
      <div className="guest-canvas-container">
        <div className="error-state-friendly">
          <div className="error-content">
            <div className="error-icon">
              <FaExclamationCircle className="error-icon-main" />
            </div>
            <h2 className="error-title">Sesión no encontrada</h2>
            <p className="error-message">
              {message || 'La sesión que buscas no existe o no tienes permisos para acceder a ella.'}
            </p>
            <div className="error-actions">
              <Button
                variant="primary"
                onClick={() => window.location.href = '/'}
                className="home-btn"
              >
                Volver al inicio
              </Button>
            </div>
            <div className="error-help">
              <FaPhone className="help-icon" />
              <p>Si crees que esto es un error, contacta al administrador</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!sessionOpen) {
    return (
      <div className="guest-canvas-container">
        <div className="session-closed-state">
          <div className="closed-icon">
            <FaCheckCircle />
          </div>
          <h2>Sesión Finalizada</h2>
          <p>¡Gracias por participar! La sesión ha sido cerrada por el anfitrión.</p>
          <p>Serás redirigido automáticamente...</p>
        </div>
      </div>
    )
  }

  const progress = getPersonalProgress()
  const progressPercentage = progress.total > 0 ? Math.round((progress.answered / progress.total) * 100) : 0

  return (
    <div className="guest-canvas-container">
      {/* Header del Invitado */}
      <div className="guest-header">
        <div className="session-info">
          <h1 className="session-title">{session.sessionName}</h1>
          {session.description && (
            <p className="session-description">{session.description}</p>
          )}
        </div>
        
        <div className="welcome-section">
          <h2 className="welcome-title">
            ¡Hola, {authenticatedUser.userFullName}!
          </h2>
          <p className="welcome-message">
            La pasaremos estupendo juntos en esta experiencia
          </p>
        </div>
        
        <div className="session-stats">
          <div className="stat-item">
            <FaClock className="stat-icon" />
            <span className="stat-value">{sessionDuration}</span>
            <span className="stat-label">Tiempo</span>
          </div>
          <div className="stat-item">
            <FaQuestionCircle className="stat-icon" />
            <span className="stat-value">{progress.answered}/{progress.total}</span>
            <span className="stat-label">Respondidas</span>
          </div>
            <div className="stat-item progress-stat">
              <div 
                className="progress-circle-small"
                style={{
                  background: `conic-gradient(#ff6b35 0deg, #f7931e ${progressPercentage * 3.6}deg, #e2e8f0 ${progressPercentage * 3.6}deg)`
                }}
              >
                <div className="progress-percentage-small">{progressPercentage}%</div>
              </div>
              <span className="stat-label">Progreso</span>
            </div>
        </div>
      </div>

      {/* Lista de Preguntas */}
      <div className="questions-container">
        <div className="questions-intro">
          <div className="header-content">
            <h3>Preguntas de la Sesión</h3>
            <p className="questions-intro-subtitle">
              Responde con imágenes que representen tu experiencia
            </p>
          </div>
        </div>

        {session.questions && session.questions.length > 0 ? (
          <div className="questions-grid">
            {session.questions.map((question, index) => {
              const isAnswered = isQuestionAnswered(question)
              const currentUser = getCurrentUser()
              const userAnswer = currentUser?.userAnswers?.find(answer => answer.sessionQstId === question.sessionQstId)
              
              return (
                <Card 
                  key={question.sessionQstId} 
                  className={`question-card ${isAnswered ? 'answered' : 'pending'}`}
                  onClick={() => handleShow(
                    question.questionId, 
                    0, // userIndex no es relevante para invitados
                    index, 
                    sessionOpen, 
                    question.responses, 
                    userAnswer?.responses || []
                  )}
                >
                  <Card.Body>
                    <div className="question-header">
                      <span className="question-index-label">{`Pregunta #${index + 1}`}</span>
                      <Badge 
                        bg={isAnswered ? 'success' : 'warning'} 
                        className="status-badge"
                      >
                        {isAnswered ? (
                          <>
                            <FaCheckCircle className="me-1" />
                            Respondida
                          </>
                        ) : (
                          <>
                            <FaHourglassHalf className="me-1" />
                            Pendiente
                          </>
                        )}
                      </Badge>
                    </div>
                    
                    <h5 className="question-text">{question.question}</h5>
                    
                    <div className="question-footer">
                      {isAnswered && (
                        <div className="response-count">
                          <FaComments className="me-1" />
                          {userAnswer?.responses?.length || 0} respuestas
                        </div>
                      )}
                      <div className="photo-count">Responder con imágenes</div>
                    </div>

                    {isAnswered && (
                      <div className="preview-responses">
                        {userAnswer?.responses?.slice(0, 3).map((response, idx) => (
                          <img 
                            key={idx}
                            src={response.image} 
                            alt="Respuesta" 
                            className="preview-image"
                          />
                        ))}
                        {userAnswer?.responses?.length > 3 && (
                          <div className="more-responses">
                            +{userAnswer.responses.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )
            })}
          </div>
        ) : (
          <div className="empty-questions">
            <FaInbox className="empty-icon" />
            <h4>No hay preguntas disponibles</h4>
            <p>El anfitrión aún no ha agregado preguntas a esta sesión</p>
          </div>
        )}

        <div className="view-all-responses-footer">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleOpenAllResponsesModal}
            className="view-all-responses-btn view-all-responses-btn--compact"
          >
            <FaEye aria-hidden />
            <span>Ver otras respuestas</span>
          </Button>
          {isInvitado && (
            <Button
              type="button"
              variant="primary"
              size="lg"
              onClick={handleExitToLogin}
              className="guest-session-exit-btn"
            >
              <FaSignOutAlt aria-hidden />
              <span>Salir de la sesión</span>
            </Button>
          )}
        </div>
      </div>

      {/* Modal de Respuesta */}
      <ResponseCanvas
        showModalResponse={showModal}
        handleClose={handleCloseModal}
        handleSelectResponse={handleOnSelectResponses}
        saveResponse={saveResponse}
        responses={qstResponses}
        imagesSelected={imagesSelected}
        handleChangePhrase={handleChangePhrase}
      />

      {/* Modal de Respuestas de Todos */}
      <Modal
        show={showAllResponsesModal}
        onHide={handleCloseAllResponsesModal}
        size="xl"
        centered
        className="all-responses-modal"
      >
        <Modal.Header closeButton className="all-responses-header">
          <div className="modal-header-content">
            <div className="modal-icon">
              <FaUsers />
            </div>
            <div className="modal-title-content">
              <Modal.Title>Perspectivas Compartidas</Modal.Title>
              <p className="modal-subtitle">
                Qué maravilloso explorar cómo cada persona percibe esta experiencia de manera única
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="all-responses-body">
          {/* Selector de Preguntas */}
          <div className="question-selector">
            <h5 className="selector-title">Selecciona una pregunta</h5>
            <div className="questions-tabs">
              {session.questions && session.questions.map((question, index) => (
                <button
                  key={question.sessionQstId}
                  onClick={() => handleSelectQuestionForView(question)}
                  className={`question-tab ${selectedQuestionForView?.sessionQstId === question.sessionQstId ? 'active' : ''}`}
                >
                  <span className="tab-number">#{index + 1}</span>
                  <span className="tab-text">{question.question}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Respuestas de los Participantes */}
          {selectedQuestionForView && (
            <div className="participants-responses">
              <div className="responses-header">
                <h5>Respuestas a: "{selectedQuestionForView.question}"</h5>
                <span className="participants-count">
                  {session.sessionUsers?.filter(user => {
                    const userAnswer = user.userAnswers?.find(answer => answer.sessionQstId === selectedQuestionForView.sessionQstId)
                    return userAnswer && userAnswer.responses && userAnswer.responses.length > 0
                  }).length} participantes han respondido
                </span>
              </div>
              
              <div className="responses-grid">
                {session.sessionUsers && session.sessionUsers.map((user) => {
                  const userAnswer = user.userAnswers?.find(answer => answer.sessionQstId === selectedQuestionForView.sessionQstId)
                  const hasResponses = userAnswer && userAnswer.responses && userAnswer.responses.length > 0
                  
                  if (!hasResponses) return null
                  
                  return (
                    <div key={user.userId} className="participant-card">
                      <div className="participant-info">
                        <div className="participant-avatar">
                          {user.userFullName.charAt(0).toUpperCase()}
                        </div>
                        <div className="participant-details">
                          <div className="participant-name">{user.userFullName}</div>
                          <span className="response-count">
                            <FaImages className="me-1" />
                            {userAnswer.responses.length} imagen{userAnswer.responses.length !== 1 ? 'es' : ''}
                          </span>
                        </div>
                      </div>
                      
                      <div className="participant-responses">
                        {userAnswer.responses.slice(0, 3).map((response, idx) => (
                          <div 
                            key={idx} 
                            className="response-thumbnail"
                            onClick={() => handleImageDetailClick(
                              response.image, 
                              user.userFullName, 
                              response.phrase,
                              selectedQuestionForView.question
                            )}
                          >
                            <img 
                              src={response.image} 
                              alt="Respuesta" 
                            />
                            <div className="thumbnail-overlay">
                              <FaExpandAlt />
                            </div>
                            {response.phrase && (
                              <div className="thumbnail-phrase">"{response.phrase}"</div>
                            )}
                          </div>
                        ))}
                        {userAnswer.responses.length > 3 && (
                          <div className="more-responses-indicator">
                            +{userAnswer.responses.length - 3} más
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {session.sessionUsers?.filter(user => {
                const userAnswer = user.userAnswers?.find(answer => answer.sessionQstId === selectedQuestionForView.sessionQstId)
                return userAnswer && userAnswer.responses && userAnswer.responses.length > 0
              }).length === 0 && (
                <div className="no-responses">
                  <FaInbox className="no-responses-icon" />
                  <h4>Aún no hay respuestas</h4>
                  <p>Sé el primero en compartir tu perspectiva</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal de Detalle de Imagen */}
      <Modal
        show={showImageDetailModal}
        onHide={handleCloseImageDetail}
        size="lg"
        centered
        className="image-detail-modal"
      >
        <Modal.Body>
          {selectedImageDetail && (
            <div className="image-detail-container">
              <div className="image-detail-header">
                <h5 className="image-detail-title">
                  Respuesta de {selectedImageDetail.userName}
                </h5>
                <button 
                  className="image-detail-close"
                  onClick={handleCloseImageDetail}
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="image-detail-question">
                <FaQuestionCircle className="me-2" />
                <span>{selectedImageDetail.questionText}</span>
              </div>
              
              <img 
                src={selectedImageDetail.src} 
                alt="Imagen ampliada" 
                className="image-detail-image"
              />
              
              {selectedImageDetail.phrase && (
                <div className="image-detail-caption">
                  <p className="image-detail-phrase">"{selectedImageDetail.phrase}"</p>
                  <p className="image-detail-user">— {selectedImageDetail.userName}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  )
}
